<template>
  <v-form ref="form" v-model="formValid" @submit.prevent="submitApplication">
    <v-text-field
      v-model="application.firstName"
      :label="$t('vacancies.form.firstName', 'Voornaam') + ' *'"
      :rules="[v => !!v || $t('vacancies.form.firstNameRequired', 'Voornaam is verplicht')]"
      required
    />
    <v-text-field
      v-model="application.lastName"
      :label="$t('vacancies.form.lastName', 'Achternaam') + ' *'"
      :rules="[v => !!v || $t('vacancies.form.lastNameRequired', 'Achternaam is verplicht')]"
      required
    />
    <v-text-field
      v-model="application.email"
      :label="$t('vacancies.form.email', 'E-mail') + ' *'"
      type="email"
      :rules="[v => !!v || $t('vacancies.form.emailRequired', 'E-mail is verplicht'), v => /.+@.+\..+/.test(v) || $t('vacancies.form.emailValid', 'E-mail moet geldig zijn')]"
      required
    />
    <v-text-field
      v-model="application.phone"
      :label="$t('vacancies.form.phone', 'Telefoonnummer') + ' *'"
      :rules="[v => !!v || $t('vacancies.form.phoneRequired', 'Telefoonnummer is verplicht'), v => /^\+?[0-9\s\-]+$/.test(v) || $t('vacancies.form.phoneValid', 'Telefoonnummer moet geldig zijn')]"
      required
    />

    <!-- Resume (CV) - driven by applicationSettings.resume -->
    <v-file-input
      v-if="settings.resume !== 'HIDDEN'"
      v-model="application.cv"
      :label="$t('vacancies.form.cvUpload', 'CV uploaden') + (settings.resume === 'REQUIRED' ? ' *' : '')"
      accept=".pdf,.doc,.docx"
      prepend-icon="mdi-paperclip"
      :rules="settings.resume === 'REQUIRED' ? [v => !!v || $t('vacancies.form.cvRequired', 'CV is verplicht')] : []"
      :required="settings.resume === 'REQUIRED'"
    />

    <!-- Cover Letter - driven by applicationSettings.coverLetter -->
    <v-textarea
      v-if="settings.coverLetter !== 'HIDDEN'"
      v-model="application.coverLetter"
      :label="$t('vacancies.form.coverLetter', 'Motivatiebrief') + (settings.coverLetter === 'REQUIRED' ? ' *' : '')"
      rows="3"
      :rules="settings.coverLetter === 'REQUIRED' ? [v => !!v || $t('vacancies.form.coverLetterRequired', 'Motivatiebrief is verplicht')] : []"
      :required="settings.coverLetter === 'REQUIRED'"
    />

    <!-- Remarks - driven by applicationSettings.remarks -->
    <v-textarea
      v-if="settings.remarks !== 'HIDDEN'"
      v-model="application.remarks"
      :label="$t('vacancies.form.remarks', 'Opmerkingen') + (settings.remarks === 'REQUIRED' ? ' *' : '')"
      rows="2"
      :rules="settings.remarks === 'REQUIRED' ? [v => !!v || $t('vacancies.form.remarksRequired', 'Opmerkingen zijn verplicht')] : []"
      :required="settings.remarks === 'REQUIRED'"
    />

    <!-- Interview Consent - driven by applicationSettings.interviewConsent -->
    <v-checkbox
      v-if="settings.interviewConsent !== 'HIDDEN'"
      v-model="application.consentInterview"
      :rules="settings.interviewConsent === 'REQUIRED' ? [v => !!v || $t('vacancies.form.consentRequired', 'Toestemming is verplicht')] : []"
      :required="settings.interviewConsent === 'REQUIRED'"
    >
      <template v-slot:label>
        <div>
          {{ $t('vacancies.form.consentInterview', 'Ik geef toestemming voor een screening interview en ga akkoord met de') }}
          <a href="https://jaicob.ai/legal/privacy" target="_blank" @click.stop>{{ $t('vacancies.form.privacyPolicy', 'Privacyverklaring') }}</a>
        </div>
      </template>
    </v-checkbox>

    <v-checkbox
      v-model="application.privacyAgreed"
      :rules="[v => !!v || $t('vacancies.form.privacyRequired', 'Je moet akkoord gaan met het privacybeleid')]"
      required
    >
      <template v-slot:label>
        <div>
          {{ $t('vacancies.form.privacyAgree', 'Ik ga akkoord met de') }}
          <a href="https://www.bridgebv.com/privacyverklaring" target="_blank" @click.stop>{{ $t('vacancies.form.privacyPolicy', 'Privacyverklaring') }}</a>
          {{ $t('vacancies.form.privacyText', 'en geef toestemming voor het verwerken en opslaan van mijn persoonsgegevens') }}
        </div>
      </template>
    </v-checkbox>

    <v-btn color="primary" block :disabled="!formValid" :loading="submitting" @click="submitApplication">
      {{ $t('vacancies.detail.sendApplication', 'Verstuur sollicitatie') }}
    </v-btn>
  </v-form>
</template>

<script setup>
import { vacancyRepository } from '@/layers/vacancies/repositories/vacancyRepository'

const { t } = useI18n()

const props = defineProps({
  vacancy: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['submitted', 'error'])

const form = ref(null)
const formValid = ref(false)
const submitting = ref(false)

const settings = computed(() => ({
  resume: props.vacancy.applicationSettings?.resume ?? 'OPTIONAL',
  coverLetter: props.vacancy.applicationSettings?.coverLetter ?? 'HIDDEN',
  remarks: props.vacancy.applicationSettings?.remarks ?? 'HIDDEN',
  interviewConsent: props.vacancy.applicationSettings?.interviewConsent ?? 'HIDDEN',
}))

const application = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  coverLetter: '',
  remarks: '',
  cv: null,
  consentInterview: false,
  privacyAgreed: false
})

const submitApplication = async () => {
  const { valid } = await form.value.validate()
  if (!valid) return

  submitting.value = true
  const formData = new FormData()
  formData.append('firstName', application.value.firstName)
  formData.append('lastName', application.value.lastName)
  formData.append('email', application.value.email)
  formData.append('phone', application.value.phone || '')

  if (settings.value.coverLetter !== 'HIDDEN' && application.value.coverLetter) {
    formData.append('coverLetter', application.value.coverLetter)
  }
  if (settings.value.remarks !== 'HIDDEN' && application.value.remarks) {
    formData.append('remarks', application.value.remarks)
  }
  if (settings.value.resume !== 'HIDDEN' && application.value.cv) {
    formData.append('cv', application.value.cv)
  }
  if (settings.value.interviewConsent !== 'HIDDEN') {
    formData.append('consentInterview', application.value.consentInterview ? '1' : '0')
  }
  formData.append('privacyAgreed', '1')

  try {
    await vacancyRepository.submitApplication(props.vacancy.id, formData)
    emit('submitted')
    form.value.reset()
    application.value.cv = null
  } catch (error) {
    console.error('Application submission failed:', error)
    emit('error', error)
  } finally {
    submitting.value = false
  }
}
</script>
